import { NotificationService, useIntl, useIsMounted, UserCredentials } from '@capsicum/common';
import { Alert, Button, Divider, ModalService, Snackbar, Timer, useIdParam, useRouter } from '@capsicum/common-web';
import { AccessIcon, InfoIcon } from '@capsicum/common-web/icons';
import {
	type BidNotificationHandlerProps,
	placeOrderPayloadAtom,
	setProductQuantityAtom,
	useRPAuctionDetails,
	useRPAuctionsCommon
} from '@capsicum/features/fanshop/shared';
import { InsufficientRPDrawer } from '@capsicum/features/fanshop/web';
import { ApprovedFooter } from '@capsicum/features/regulations/web';
import { RequirementsPageCredentialsNeeded, useNCWBalance } from '@capsicum/socios-core';
import { useBuySellButtons, useCredentialsHandler } from '@capsicum/socios-core-web';
import { useSetAtom } from 'jotai';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { AuctionBidNotAllowedDrawer, BidStatusCardRP, LatestBidListRP } from '../../ui';

import * as S from './RPAuctionDetails.styled';

export const RPAuctionDetails = ({ params }: { params: { id: string } }) => {
	const { colors, sizing } = useTheme();
	const router = useRouter();
	const setProductQuantity = useSetAtom(setProductQuantityAtom);
	const setPlaceOrderPayload = useSetAtom(placeOrderPayloadAtom);
	const { $t } = useIntl();
	const { id: auctionId } = useIdParam(params, '/fan-shop/rewards');
	const [activeImage, setActiveImage] = useState<number>(0);
	const [showFirstTimer, setShowFirstTimer] = useState(true);

	const onThumbnailChange = (index: number) => {
		setActiveImage(index);
	};

	const { getTokenBalanceBy, isPending: isPendingNCWallet } = useNCWBalance();

	const bidNotificationHandler = useCallback(({ description, title, variant }: BidNotificationHandlerProps) => {
		NotificationService.clearNotifications();
		NotificationService.addNotification({
			component: (
				<Snackbar
					description={description}
					testID="auctionDetails-acceptedBid"
					title={title}
					variant={variant}
				/>
			)
		});
	}, []);

	const isMounted = useIsMounted();

	const {
		allImages,
		auctionData,
		auctionEndDate,
		auctionStartDate,
		availableRPBalance,
		currentBid,
		currentPartner,
		eventDate,
		faqLink,
		getCategory,
		getIsOwnBidByUserId,
		getMinSsuAmount,
		hasBids,
		hasCheckoutCompleted,
		hasEnoughFanTokens,
		hasEnoughSsu,
		hasEventDate,
		hasFinished,
		hasLinkedProduct,
		hasLinkedProductOrder,
		hasStarted,
		isActive,
		isBidAllowed,
		isBidEnabled,
		isExperienceType,
		isOwnBid,
		isPendingCWallet,
		isPhysicalTicket,
		isSkeletonLoading,
		isTicketType,
		matchPlace,
		nextBid,
		productDescription,
		refetchAuction,
		setCurrentStatus,
		texts
	} = useRPAuctionDetails({
		auctionId: auctionId || '',
		bidNotificationHandler,
		getTokenBalanceBy,
		isWebSocketDismissed: !isMounted()
	});

	const isNotPendingCombinedWallets = !isPendingCWallet && !isPendingNCWallet;

	const { handleCredential: onEnoughRPButtonPress } = useCredentialsHandler({
		credentialCodes: UserCredentials.RP_AUCTIONS_BID,
		onCredentialGranted: () => {
			router.push(`/fan-shop/rewards/auction/${auctionId}/bid`);
		},
		page: RequirementsPageCredentialsNeeded.auctionBid
	});

	const handleNavigation = useCallback(async () => {
		setProductQuantity(auctionData?.availableStock ?? 0);
		setPlaceOrderPayload({});
		router.push(`/fan-shop/rewards/order-details/${auctionData?.id}`);
	}, [auctionData, router]);

	const onInsufficientRewardsPoints = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.preventDefault();
			e.stopPropagation();

			ModalService.addModal({
				renderComponent: ({ close }) => (
					<InsufficientRPDrawer
						amount={getMinSsuAmount()}
						closeModal={close}
						description={texts.insufficientRPDescription}
						testID="rp-auction-details-insufficient-reward-points-drawer"
						title={texts.insufficientRPTitle}
						walletAmount={availableRPBalance}
						onPress={() => router.push(`/nc-wallet`)}
					/>
				)
			});
		},
		[texts, getMinSsuAmount, availableRPBalance]
	);

	const handleOnPlaceABidClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			if (!isBidAllowed) {
				ModalService.addModal({
					renderComponent: ({ close }) => <AuctionBidNotAllowedDrawer onButtonPress={close} />
				});

				return;
			}

			if (!hasEnoughSsu) {
				onInsufficientRewardsPoints(e);

				return;
			}

			onEnoughRPButtonPress();
		},
		[isBidAllowed, hasEnoughSsu, onEnoughRPButtonPress, onInsufficientRewardsPoints]
	);

	const imageWrapper = useMemo(
		() => (
			<S.ImageWrapper>
				<S.ImageContainer>
					{allImages?.[activeImage]?.url ? (
						<S.ProductImage
							alt={`${auctionData?.label} image`}
							blurhash={allImages?.[activeImage]?.blurHash}
							height={303}
							optimized
							src={allImages?.[activeImage]?.url || ''}
							width={256}
						/>
					) : (
						<S.StyledCardImagePlaceholder />
					)}
				</S.ImageContainer>
				{allImages?.length > 1 && (
					<S.GalleryView>
						{allImages?.map((image, index) => (
							<S.Pressable
								$selected={activeImage === index}
								data-testid={`auction-galleryView-image${index}`}
								key={index}
								onClick={() => onThumbnailChange(index)}
							>
								<S.StyledImage
									alt={`${auctionData?.label ?? 'default-label'}-image${index}`}
									blurhash={image?.blurHash}
									height={70}
									optimized
									src={image?.url || ''}
									width={90}
								/>
							</S.Pressable>
						))}
					</S.GalleryView>
				)}
			</S.ImageWrapper>
		),
		[allImages, activeImage, allImages?.[activeImage]?.url]
	);

	const timer = useMemo(
		() =>
			!hasStarted && showFirstTimer ? (
				<Timer
					customFormat="digitalTimer"
					expirationDate={auctionStartDate}
					key="comingCoonAuction-timer"
					type="default"
					onElapsed={() => {
						refetchAuction();
						setShowFirstTimer(false);
					}}
				/>
			) : (
				<Timer
					closedLabel
					customFormat="digitalTimer"
					expirationDate={auctionEndDate}
					key="startAuction-timer"
					type="default"
					onElapsed={() => {
						refetchAuction();
						setCurrentStatus('closed');
					}}
				/>
			),
		[auctionEndDate, auctionStartDate, texts.closed, showFirstTimer, hasStarted]
	);

	const { bidTitle } = useRPAuctionsCommon({ isAuctionPage: true, isOwnBid, variant: auctionData });

	const bidStatus = useMemo(
		() => (
			<BidStatusCardRP
				bidTitle={bidTitle}
				hasBid={(auctionData?.auctionBids?.length || 0) > 0}
				hasFinished={hasFinished}
				isOwnBid={isOwnBid}
				priceCurrentBid={currentBid}
				priceNextBid={nextBid}
			/>
		),
		[auctionData?.auctionBids, bidTitle, isOwnBid, currentBid, nextBid]
	);

	const latestBidList = useMemo(
		() =>
			hasBids && (
				<>
					<LatestBidListRP
						bidParticipants={auctionData?.auctionBidsSorted || []}
						isMyBid={getIsOwnBidByUserId}
					/>
					<Divider orientation="horizontal" />
				</>
			),
		[auctionId, hasBids, getIsOwnBidByUserId, currentBid, auctionData?.auctionBids]
	);

	const button = useMemo(
		() =>
			isActive && (
				<S.ButtonWrapper>
					<Button
						color="brand"
						disabled={!isBidEnabled}
						isLoading={isSkeletonLoading}
						testID="auction-button-place-bid"
						text={texts.placeABid}
						onClick={handleOnPlaceABidClick}
					/>
				</S.ButtonWrapper>
			),
		[isActive, texts.placeABid, isSkeletonLoading, isBidEnabled, handleOnPlaceABidClick]
	);

	const redeemButton = useMemo(
		() =>
			isOwnBid &&
			!isActive &&
			!hasCheckoutCompleted && (
				<S.ButtonWrapper>
					<Button
						color="brand"
						disabled={!auctionData?.winningFundsTransferred}
						isLoading={isSkeletonLoading}
						testID="auction-button-redeem"
						text={auctionData?.winningFundsTransferred ? texts.redeem : texts.processing}
						onClick={handleNavigation}
					/>
				</S.ButtonWrapper>
			),
		[
			isActive,
			texts.redeem,
			texts.processing,
			isSkeletonLoading,
			isOwnBid,
			hasCheckoutCompleted,
			auctionData?.winningFundsTransferred
		]
	);

	const informationBanner = useMemo(
		() => (
			<Alert
				alignIcon="flex-start"
				closable={false}
				icon={InfoIcon}
				subTitle={
					isTicketType || isExperienceType
						? $t('rewards.fanshop.linkedProductsBanner.suspendedTicketText')
						: $t('rewards.fanshop.linkedProductsBanner.suspendedDefaultText')
				}
				testID="auction-information-banner"
				title={$t('rewards.fanshop.linkedProductsBanner.title')}
				type="important"
			/>
		),
		[$t, isTicketType, isExperienceType]
	);

	const { buy } = useBuySellButtons(currentPartner?.currencyId || '');

	const tokenParticipationAmount = useMemo(
		() =>
			!hasEnoughFanTokens() &&
			isNotPendingCombinedWallets && (
				<S.BuyTokensLabel>
					<AccessIcon color={colors.icons.main.enabled} width={sizing.xsmall} />
					<S.TokensNeededText>{texts.tokensNeeded}</S.TokensNeededText>
					<S.StyledTextButton
						color="brand"
						testID="auction-buyFanToken-button"
						text={texts.buyFanTokens}
						textSize="small"
						onClick={buy}
					/>
				</S.BuyTokensLabel>
			),
		[$t, hasEnoughFanTokens, isNotPendingCombinedWallets, buy]
	);

	const description = useMemo(
		() => (
			<div>
				<S.BlockTitle>{texts.details}</S.BlockTitle>
				<S.Description>{productDescription}</S.Description>
			</div>
		),
		[texts.details, productDescription]
	);

	const importantInformation = useMemo(
		() =>
			isTicketType && (
				<div>
					<S.BlockTitle>{texts.importantInformation}</S.BlockTitle>
					<S.Description>{texts.accompaniedMinors}</S.Description>
					<S.Description>{texts.prohibitedResale}</S.Description>
				</div>
			),
		[isTicketType]
	);

	const faq = useMemo(
		() => (
			<div>
				<S.BlockTitle>{texts.faqTitle}</S.BlockTitle>
				<S.Description>
					{$t('rewards.rpAuctions.faqDescription', {
						link: (chunks: Array<ReactNode>) => (
							<S.Link data-testid="faq-link-auction" href={faqLink} target="_blank">
								{chunks}
							</S.Link>
						)
					})}
				</S.Description>
			</div>
		),
		[texts.faqTitle]
	);

	const categoryInfo = useMemo(
		() => (
			<S.CategoryInfo>
				{(isExperienceType && hasEventDate) || isTicketType || isPhysicalTicket ? (
					<>
						<S.InfoWrapper>
							<S.DetailsTitle>{isExperienceType ? texts.eventDate : texts.matchDate}</S.DetailsTitle>
							<S.DetailsValue>{eventDate}</S.DetailsValue>
						</S.InfoWrapper>
						{isTicketType && (
							<div>
								<S.DetailsTitle>{texts.stadium}</S.DetailsTitle>
								<S.DetailsValue>{matchPlace}</S.DetailsValue>
							</div>
						)}
					</>
				) : null}
			</S.CategoryInfo>
		),
		[isTicketType, isPhysicalTicket, texts, matchPlace, eventDate]
	);

	return (
		<S.Wrapper>
			<S.ProductUkHeaderWarning horizontalPadding page="rewardsAuctionProduct" verticalPadding />
			<S.CategoryTitle>{$t('rewards.rpAuctions.title')}</S.CategoryTitle>
			<S.MainContainer>
				{imageWrapper}
				<S.Content>
					<S.HeaderWrapper>
						<S.TitleWrapper>
							<S.Category>{getCategory(auctionData)}</S.Category>
							<S.Title>{auctionData?.label}</S.Title>
						</S.TitleWrapper>
						{isSkeletonLoading ? null : timer}
					</S.HeaderWrapper>
					{tokenParticipationAmount}
					{bidStatus}
					<Divider orientation="horizontal" />
					{latestBidList}
					{hasLinkedProductOrder && hasLinkedProduct && (
						<S.InformationWrapper>{informationBanner}</S.InformationWrapper>
					)}
					{categoryInfo}
					{description}
					{importantInformation}
					{faq}
					{button}
					{redeemButton}
				</S.Content>
			</S.MainContainer>
			<ApprovedFooter page="rewardsAuctionProduct" />
		</S.Wrapper>
	);
};
